
import React, { useState, useEffect } from 'react';
import { useSignalData } from '../context/SignalContext';
import { Save, Settings, Shield, DollarSign, Layers } from 'lucide-react';

export const SettingsView: React.FC = () => {
  const { systemSettings, updateSettings } = useSignalData();
  const [localSettings, setLocalSettings] = useState(systemSettings);
  const [activeSection, setActiveSection] = useState<'GENERAL' | 'COMMISSION' | 'PAYOUT'>('COMMISSION');
  const [isSaved, setIsSaved] = useState(false);

  // Cập nhật localSettings khi systemSettings thay đổi
  useEffect(() => {
    if (systemSettings && Object.keys(systemSettings).length > 0) {
      setLocalSettings(systemSettings);
    }
  }, [systemSettings]);

  const handleSave = () => {
    updateSettings(localSettings);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  // Kiểm tra nếu settings chưa load xong
  if (!localSettings || !localSettings.commission || !localSettings.payout || !localSettings.general) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Đang tải cấu hình...</p>
        </div>
      </div>
    );
  }

  const handleChange = (section: keyof typeof localSettings, key: string, value: any) => {
    setLocalSettings({
        ...localSettings,
        [section]: {
            ...localSettings[section],
            [key]: isNaN(Number(value)) ? value : Number(value)
        }
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex justify-between items-center">
        <div>
            <h2 className="text-xl font-black text-slate-800 flex items-center gap-2 uppercase tracking-wide">
                <Settings className="w-6 h-6 text-slate-500" />
                Cấu hình hệ thống
            </h2>
            <p className="text-slate-400 text-sm mt-1">Quản lý tỷ lệ hoa hồng cố định cho các cấp bậc.</p>
        </div>
        <button 
            onClick={handleSave}
            className={`flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-white transition-all shadow-lg ${
                isSaved ? 'bg-green-600 shadow-green-600/20' : 'bg-brand-600 hover:bg-brand-700 shadow-brand-600/20 active:scale-95'
            }`}
        >
            <Save className="w-4 h-4" />
            {isSaved ? 'Đã lưu!' : 'Lưu cài đặt'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Menu */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3 h-fit space-y-1">
            <button 
                onClick={() => setActiveSection('COMMISSION')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-left transition-all ${activeSection === 'COMMISSION' ? 'bg-brand-50 text-brand-700 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}
            >
                <Layers className="w-4 h-4" /> Hoa hồng & Cấp bậc
            </button>
            <button 
                onClick={() => setActiveSection('PAYOUT')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-left transition-all ${activeSection === 'PAYOUT' ? 'bg-brand-50 text-brand-700 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}
            >
                <DollarSign className="w-4 h-4" /> Quy tắc thanh toán
            </button>
             <button 
                onClick={() => setActiveSection('GENERAL')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-left transition-all ${activeSection === 'GENERAL' ? 'bg-brand-50 text-brand-700 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}
            >
                <Shield className="w-4 h-4" /> Cấu hình chung
            </button>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3 space-y-6">
            
            {activeSection === 'COMMISSION' && (
                <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm space-y-8">
                    <div>
                        <h3 className="text-base font-black text-slate-800 mb-6 pb-2 border-b border-slate-50 uppercase tracking-widest flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            Hoa hồng giới thiệu trực tiếp (T)
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Level 1 ($)</label>
                                <input 
                                    type="number" step="0.5"
                                    value={localSettings.commission.t1_amount}
                                    onChange={(e) => handleChange('commission', 't1_amount', e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Level 2 ($)</label>
                                <input 
                                    type="number" step="0.5"
                                    value={localSettings.commission.t2_amount}
                                    onChange={(e) => handleChange('commission', 't2_amount', e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Level 3 ($)</label>
                                <input 
                                    type="number" step="0.5"
                                    value={localSettings.commission.t3_amount}
                                    onChange={(e) => handleChange('commission', 't3_amount', e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-base font-black text-slate-800 mb-6 pb-2 border-b border-slate-50 uppercase tracking-widest flex items-center gap-2">
                             <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                            Hoa hồng Hệ thống / Gián tiếp (K)
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Level 1 ($)</label>
                                <input 
                                    type="number" step="0.5"
                                    value={localSettings.commission.k1_amount}
                                    onChange={(e) => handleChange('commission', 'k1_amount', e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Level 2 ($)</label>
                                <input 
                                    type="number" step="0.5"
                                    value={localSettings.commission.k2_amount}
                                    onChange={(e) => handleChange('commission', 'k2_amount', e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Level 3 ($)</label>
                                <input 
                                    type="number" step="0.5"
                                    value={localSettings.commission.k3_amount}
                                    onChange={(e) => handleChange('commission', 'k3_amount', e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                />
                            </div>
                        </div>
                        <p className="mt-4 text-[10px] text-slate-400 italic font-medium italic">* Hoa hồng (K) sẽ được tự động cộng vào Ví Thưởng của Affiliate cấp trên.</p>
                    </div>
                </div>
            )}

            {activeSection === 'PAYOUT' && (
                <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm space-y-6">
                    <h3 className="text-base font-black text-slate-800 mb-6 pb-2 border-b border-slate-50 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        Quy tắc thanh toán
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Hạn mức rút tối thiểu ($)</label>
                            <input 
                                type="number" 
                                value={localSettings.payout.minWithdrawal}
                                onChange={(e) => handleChange('payout', 'minWithdrawal', e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Thời gian giữ tiền (Hold days)</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    value={localSettings.payout.holdDays}
                                    onChange={(e) => handleChange('payout', 'holdDays', e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold uppercase tracking-wider">ngày</span>
                            </div>
                        </div>

                         <div className="space-y-2">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Ngày thanh toán định kỳ</label>
                            <input 
                                type="text" 
                                value={localSettings.payout.payoutDates}
                                onChange={(e) => handleChange('payout', 'payoutDates', e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                placeholder="15, 30"
                            />
                        </div>
                    </div>
                </div>
            )}

            {activeSection === 'GENERAL' && (
                <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm space-y-6">
                    <h3 className="text-base font-black text-slate-800 mb-6 pb-2 border-b border-slate-50 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                        Cấu hình chung
                    </h3>
                    
                     <div className="space-y-2">
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Tên thương hiệu / Trang web</label>
                        <input 
                            type="text" 
                            value={localSettings.general.siteName}
                            onChange={(e) => handleChange('general', 'siteName', e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Thời gian lưu Cookie</label>
                             <div className="relative">
                                <input 
                                    type="number" 
                                    value={localSettings.general.cookieDuration}
                                    onChange={(e) => handleChange('general', 'cookieDuration', e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold uppercase tracking-wider">ngày</span>
                            </div>
                        </div>
                         <div className="space-y-2">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Mô hình phân bổ (Attribution)</label>
                            <select 
                                value={localSettings.general.attributionModel}
                                onChange={(e) => handleChange('general', 'attributionModel', e.target.value)}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-brand-500 outline-none appearance-none"
                            >
                                <option value="LAST_CLICK">Last Click (Nhấp cuối cùng)</option>
                                <option value="FIRST_CLICK">First Click (Nhấp đầu tiên)</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}

        </div>
      </div>
    </div>
  );
};
